'use strict';

const libPictView = require('pict-view');
const libAvatarHelper = require('./AvatarHelper.js');
const libFrameworkClient = require('../providers/FrameworkClient.js');

function _escapeHTML(pValue)
{
	return String(pValue == null ? '' : pValue)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

class PictViewTopBarUser extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this.pict.AppData.TopBarUser = this.pict.AppData.TopBarUser
			|| { LoggedInSlot: [], GuestSlot: [{}], DisplayName: '' };
	}

	_refreshSlots()
	{
		let tmpAuth = this.pict.AppData.Auth || {};
		if (tmpAuth.LoggedIn && tmpAuth.User)
		{
			this.pict.AppData.TopBarUser.LoggedInSlot = [
				{
					IDUser: tmpAuth.User.IDUser || 0,
					DisplayName: tmpAuth.User.DisplayName || tmpAuth.User.Email || tmpAuth.User.LoginID || 'You',
					Email: tmpAuth.User.Email || '',
					Color: tmpAuth.User.Color || '',
					PhotoHash: tmpAuth.User.PhotoHash || ''
				}];
			this.pict.AppData.TopBarUser.GuestSlot = [];
		}
		else
		{
			this.pict.AppData.TopBarUser.LoggedInSlot = [];
			this.pict.AppData.TopBarUser.GuestSlot = [{}];
		}
	}

	signIn()
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showAuth === 'function') tmpApp.showAuth('login');
	}

	signOut()
	{
		this._closeSessionCard();
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.signOut === 'function') tmpApp.signOut();
	}

	// The top-right chip opens a small account/session card on click: who you are and a couple of facts
	// (email, organization, role, last sign-in) plus shortcuts to the profile, settings, and sign out.
	// It is an anchored, non-blocking popover (no backdrop) appended to <body>, dismissed by clicking
	// outside, pressing Escape, choosing an action, or clicking the chip again.
	toggleSessionCard()
	{
		if (this._SessionCardEl) { this._closeSessionCard(); }
		else { this._openSessionCard(); }
	}

	_openSessionCard()
	{
		if (typeof document === 'undefined') { return; }
		let tmpAuth = this.pict.AppData.Auth || {};
		let tmpUser = (tmpAuth.LoggedIn && tmpAuth.User) ? tmpAuth.User : null;
		let tmpChip = document.getElementById('Plansheet-TopBar-Chip');
		if (!tmpUser || !tmpChip) { return; }

		let tmpEl = document.createElement('div');
		tmpEl.className = 'cst-acct-popover';
		tmpEl.innerHTML = this._sessionCardHTML(tmpUser);
		document.body.appendChild(tmpEl);
		// Fetch the account's plan sheets and fill the switcher list.
		this._loadTenants();
		this._loadInvitations();

		// Anchor below the chip, right-aligned to it and clamped into the viewport.
		let tmpRect = tmpChip.getBoundingClientRect();
		tmpEl.style.top = Math.round(tmpRect.bottom + 6) + 'px';
		tmpEl.style.right = Math.max(8, Math.round(window.innerWidth - tmpRect.right)) + 'px';

		this._SessionCardEl = tmpEl;
		// Defer wiring the dismiss handlers so the click that opened the card does not immediately close it.
		this._SessionCardOutside = (pEvent) =>
		{
			if (!this._SessionCardEl) { return; }
			if (this._SessionCardEl.contains(pEvent.target)) { return; }
			if (tmpChip.contains(pEvent.target)) { return; }
			this._closeSessionCard();
		};
		this._SessionCardKey = (pEvent) => { if (pEvent.key === 'Escape') { this._closeSessionCard(); } };
		let tmpSelf = this;
		setTimeout(function ()
		{
			document.addEventListener('mousedown', tmpSelf._SessionCardOutside, true);
			document.addEventListener('keydown', tmpSelf._SessionCardKey, true);
		}, 0);
	}

	_closeSessionCard()
	{
		if (typeof document !== 'undefined')
		{
			if (this._SessionCardOutside) { document.removeEventListener('mousedown', this._SessionCardOutside, true); }
			if (this._SessionCardKey) { document.removeEventListener('keydown', this._SessionCardKey, true); }
		}
		if (this._SessionCardEl && this._SessionCardEl.parentNode) { this._SessionCardEl.parentNode.removeChild(this._SessionCardEl); }
		this._SessionCardEl = null;
		this._SessionCardOutside = null;
		this._SessionCardKey = null;
	}

	gotoProfile()
	{
		this._closeSessionCard();
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showProfile === 'function') { tmpApp.showProfile(); }
	}

	gotoSettings()
	{
		this._closeSessionCard();
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showSection === 'function') { tmpApp.showSection('Settings'); }
	}

	// --- Tenant switcher (plan sheets) ---------------------------------------
	// The account popover lists the human's personas (one per plan sheet) and
	// lets them switch, create a new plan sheet they administer, or leave one.
	// Each action hits the server then reloads, so the whole app re-bootstraps
	// into the now-active tenant (the session cookie carries the new persona).

	_tenantAPI(pPath, pMethod, pBody)
	{
		let tmpURL = '/1.0/' + String(pPath).replace(/^\//, '');
		let tmpMethod = String(pMethod || 'GET').toUpperCase();
		let tmpRequest;
		if (tmpMethod === 'POST') { tmpRequest = libFrameworkClient.postJSON(this.pict, tmpURL, pBody || {}); }
		else if (tmpMethod === 'PUT') { tmpRequest = libFrameworkClient.putJSON(this.pict, tmpURL, pBody || {}); }
		else if (tmpMethod === 'DELETE') { tmpRequest = libFrameworkClient.delJSON(this.pict, tmpURL); }
		else { tmpRequest = libFrameworkClient.getJSON(this.pict, tmpURL); }
		// Through the shared RestClient so tenant actions recover on a 401. Keep
		// the { ok, body } contract callers read: a >= 400 rejects, which we map to
		// ok:false with the server's Error surfaced in body (FrameworkClient's error
		// carries body.Error as its message).
		return tmpRequest.then(
			(pBodyResult) => ({ ok: true, body: pBodyResult || {} }),
			(pError) => ({ ok: false, body: { Error: (pError && pError.message) || 'Request failed.' } }));
	}

	_loadTenants()
	{
		this._tenantAPI('Account/Tenants').then((pResult) =>
		{
			if (pResult.ok && pResult.body && Array.isArray(pResult.body.Tenants)) { this._renderTenants(pResult.body.Tenants); }
		}).catch(() => { /* keep the placeholder on error */ });
	}

	_renderTenants(pTenants)
	{
		if (typeof document === 'undefined') { return; }
		let tmpEl = document.getElementById('cst-acct-tenant-list');
		if (!tmpEl) { return; }
		if (!pTenants.length) { tmpEl.innerHTML = '<div class="cst-acct-tenant-empty">No plan sheets.</div>'; return; }
		// The server orders these pinned-first, then by recency (WI #339); the flyout renders that order
		// directly. Show at most 8 in the compact popover, but never let the plan sheet you are currently
		// in fall below the fold: if it is not among the first 8 (only possible with 8+ pinned), append it.
		let tmpCompact = pTenants.slice(0, 8);
		let tmpCurrent = pTenants.find((pTenant) => pTenant.IsCurrent);
		if (tmpCurrent && tmpCompact.indexOf(tmpCurrent) < 0) { tmpCompact.push(tmpCurrent); }
		let tmpSelf = "_Pict.views['Plansheet-TopBar-User']";
		tmpEl.innerHTML = tmpCompact.map((pTenant) => this._tenantRowHTML(pTenant, tmpSelf)).join('');
		// "View all" now opens the cross-tenant home (WI #339) instead of the old dead-end modal.
		let tmpViewAll = document.getElementById('cst-acct-tenant-viewall');
		if (tmpViewAll)
		{
			// Always offer the way to the full cross-tenant home, not only when the compact list overflows 8.
			tmpViewAll.style.display = '';
			tmpViewAll.textContent = (pTenants.length > 8) ? ('View all ' + pTenants.length + ' plan sheets') : 'Your plan sheets';
		}
	}

	// A pin/unpin glyph (filled when pinned) used on each flyout row; matches the home's pin control.
	_pinSVG(pOn)
	{
		return '<svg viewBox="0 0 16 16" width="14" height="14" fill="' + (pOn ? 'currentColor' : 'none') + '" stroke="currentColor" stroke-width="1.4">'
			+ '<path d="M5 2h6l-1 5 3 3H3l3-3-1-5z"/><path d="M8 10v4"/></svg>';
	}

	_tenantRowHTML(pTenant, pSelf)
	{
		let tmpSelf = pSelf || "_Pict.views['Plansheet-TopBar-User']";
		let tmpClick = pTenant.IsCurrent ? '' : ' onclick="' + tmpSelf + '.switchTenant(' + (pTenant.IDCustomer || 0) + ')"';
		let tmpIsAdmin = pTenant.IsCurrent && (pTenant.RoleLabel === 'Owner' || pTenant.RoleLabel === 'Admin' || pTenant.RoleLabel === 'Platform Admin');
		// A pin toggle on every row (pinned-first ordering is set by the server; this makes it legible and
		// actionable straight from the switcher). stopPropagation so pinning never triggers the row switch.
		let tmpPin = '<button class="cst-acct-tenant-pin' + (pTenant.Pinned ? ' on' : '') + '" title="' + (pTenant.Pinned ? 'Unpin' : 'Pin to the top') + '" onclick="event.stopPropagation();' + tmpSelf + '.togglePin(' + (pTenant.IDCustomer || 0) + ')">' + this._pinSVG(pTenant.Pinned) + '</button>';
		let tmpRight = pTenant.IsCurrent
			? ((tmpIsAdmin ? '<button class="cst-acct-tenant-invite" title="Invite someone to this plan sheet" onclick="' + tmpSelf + '.inviteToTenant()">Invite</button>' : '') +
				'<button class="cst-acct-tenant-leave" title="Leave this plan sheet" onclick="' + tmpSelf + '.leaveTenant(' + (pTenant.IDCustomer || 0) + ')">Leave</button>')
			: '<span class="cst-acct-tenant-go">Switch</span>';
		return '<div class="cst-acct-tenant' + (pTenant.IsCurrent ? ' is-current' : '') + '"' + tmpClick + '>' +
				'<span class="cst-acct-tenant-main">' +
					'<span class="cst-acct-tenant-name">' + _escapeHTML(pTenant.CustomerName || 'Untitled') + '</span>' +
					'<span class="cst-acct-tenant-sub">as ' + _escapeHTML(pTenant.DisplayName || '') + ' &middot; ' + _escapeHTML(pTenant.RoleLabel || 'Member') + '</span>' +
				'</span>' + tmpPin + tmpRight +
			'</div>';
	}

	// "View all N plan sheets" from the switcher: go to the cross-tenant home (WI #339). This replaces
	// the old modal, which was itself a dead end for the overflow it was meant to fix.
	viewAllPlansheets()
	{
		this._closeSessionCard();
		let tmpApp = this.pict && this.pict.PictApplication;
		if (tmpApp && typeof tmpApp._navigate === 'function') { tmpApp._navigate('/home'); return; }
		if (typeof window !== 'undefined' && window.location) { window.location.hash = '#/home'; }
	}

	// Pin/unpin a plan sheet straight from the switcher, then reload the list so the pinned-first order
	// updates. Empty body = toggle (the server flips the current state).
	togglePin(pIDCustomer)
	{
		let tmpID = parseInt(pIDCustomer, 10) || 0;
		if (!tmpID) { return; }
		this._tenantAPI('Account/Tenants/' + tmpID + '/Pin', 'POST', {}).then((pResult) =>
		{
			if (pResult.ok && pResult.body && pResult.body.Success) { this._loadTenants(); return; }
			this._toast((pResult.body && pResult.body.Error) || 'Could not update the pin.');
		}).catch(() => this._toast('Could not update the pin.'));
	}

	// Reload after the active plan sheet changed (switch / create / leave / join). The new tenant does not
	// have the record the current route points at (a specific document, a work item id), so a blind reload
	// would land on a not-found screen -- the reported route leak. The application reduces the route to the
	// feature area we were viewing before reloading; fall back to a plain reload if it is somehow absent.
	// See WI #328 (Pict-Application-Plansheet.reloadForActivePlansheetChange / _tenantSafeRoute).
	_reloadForTenantChange()
	{
		let tmpApp = this.pict && this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.reloadForActivePlansheetChange === 'function') { tmpApp.reloadForActivePlansheetChange(); return; }
		if (typeof window !== 'undefined' && window.location) { window.location.reload(); }
	}

	switchTenant(pIDCustomer)
	{
		this._tenantAPI('Session/Context/' + pIDCustomer, 'POST').then((pResult) =>
		{
			if (pResult.ok && pResult.body && pResult.body.Switched) { this._reloadForTenantChange(); return; }
			this._toast((pResult.body && pResult.body.Error) || 'Could not switch plan sheet.');
		}).catch(() => this._toast('Could not switch plan sheet.'));
	}

	createTenant()
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.show !== 'function') { return; }
		this._closeSessionCard();
		// Reached from the desktop popover (no modal open, a no-op) or the mobile menu modal; in the
		// latter case close it first so the create form is not stacked on top of the menu.
		if (typeof tmpModal.dismissModals === 'function') { tmpModal.dismissModals(); }
		let tmpInputID = 'cst-new-tenant-name';
		let tmpName = '';
		tmpModal.show(
			{
				title: 'Create a plan sheet',
				content: '<div class="cst-tenant-form">' +
					'<label for="' + tmpInputID + '">Name</label>' +
					'<input id="' + tmpInputID + '" type="text" autocomplete="off" maxlength="200" placeholder="e.g. The Band, Q3 Roadmap" />' +
					'<p class="cst-tenant-form-hint">You will be its administrator. Switch between plan sheets any time from your account menu.</p>' +
				'</div>',
				buttons: [ { Hash: 'create', Label: 'Create plan sheet', Style: 'primary' }, { Hash: 'cancel', Label: 'Cancel' } ],
				closeable: true,
				onOpen: () =>
				{
					let tmpInput = document.getElementById(tmpInputID);
					if (!tmpInput) { return; }
					tmpInput.focus();
					tmpInput.addEventListener('input', () => { tmpName = String(tmpInput.value || '').trim(); });
				}
			}).then((pHash) =>
			{
				if (pHash !== 'create' || !tmpName) { return; }
				this._tenantAPI('Account/Tenants', 'POST', { Name: tmpName }).then((pResult) =>
				{
					if (pResult.ok && pResult.body && pResult.body.Success) { this._reloadForTenantChange(); return; }
					this._toast((pResult.body && pResult.body.Error) || 'Could not create the plan sheet.');
				}).catch(() => this._toast('Could not create the plan sheet.'));
			});
	}

	leaveTenant(pIDCustomer)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let fDo = () =>
		{
			this._tenantAPI('Account/Tenants/' + pIDCustomer + '/Leave', 'POST').then((pResult) =>
			{
				if (pResult.ok && pResult.body && pResult.body.Success) { this._reloadForTenantChange(); return; }
				this._toast((pResult.body && pResult.body.Error) || 'Could not leave the plan sheet.');
			}).catch(() => this._toast('Could not leave the plan sheet.'));
		};
		this._closeSessionCard();
		if (tmpModal && typeof tmpModal.confirm === 'function')
		{
			tmpModal.confirm('Leave this plan sheet? Your name stays on the work you did here, but you lose access until someone adds you back.',
				{ title: 'Leave plan sheet?', confirmLabel: 'Leave', cancelLabel: 'Stay', dangerous: true })
				.then((pOk) => { if (pOk) { fDo(); } });
		}
		else { fDo(); }
	}

	_toast(pMessage)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast(pMessage, { type: 'error' }); }
		else if (typeof window !== 'undefined') { window.alert(pMessage); }
	}

	// --- Invitations ---------------------------------------------------------
	// Invitee side: pending invitations appear in the account card; Join creates a
	// persona in that plan sheet and switches in. Inviter side: an Invite action on
	// the current plan sheet (admins only) sends an invitation by email.

	_loadInvitations()
	{
		this._tenantAPI('Account/Invitations').then((pResult) =>
		{
			if (pResult.ok && pResult.body && Array.isArray(pResult.body.Invitations)) { this._renderInvitations(pResult.body.Invitations); }
		}).catch(() => { /* leave the section hidden on error */ });
	}

	_renderInvitations(pInvites)
	{
		if (typeof document === 'undefined') { return; }
		let tmpEl = document.getElementById('cst-acct-invites');
		if (!tmpEl) { return; }
		if (!pInvites.length) { tmpEl.style.display = 'none'; tmpEl.innerHTML = ''; return; }
		let tmpSelf = "_Pict.views['Plansheet-TopBar-User']";
		tmpEl.style.display = '';
		tmpEl.innerHTML = '<div class="cst-acct-tenants-h">Invitations</div>' + pInvites.map((pInvite) =>
		{
			return '<div class="cst-acct-invite">' +
					'<span class="cst-acct-tenant-main">' +
						'<span class="cst-acct-tenant-name">' + _escapeHTML(pInvite.CustomerName || 'A plan sheet') + '</span>' +
						'<span class="cst-acct-tenant-sub">invited as ' + _escapeHTML(pInvite.RoleSystemKey || 'member') + '</span>' +
					'</span>' +
					'<span class="cst-acct-invite-actions">' +
						'<button class="cst-acct-invite-accept" onclick="' + tmpSelf + '.acceptInvite(' + (pInvite.IDInvitation || 0) + ')">Join</button>' +
						'<button class="cst-acct-invite-decline" onclick="' + tmpSelf + '.declineInvite(' + (pInvite.IDInvitation || 0) + ')">Dismiss</button>' +
					'</span>' +
				'</div>';
		}).join('');
	}

	acceptInvite(pIDInvitation)
	{
		this._tenantAPI('Account/Invitations/' + pIDInvitation + '/Accept', 'POST').then((pResult) =>
		{
			if (pResult.ok && pResult.body && pResult.body.Success) { this._reloadForTenantChange(); return; }
			this._toast((pResult.body && pResult.body.Error) || 'Could not join the plan sheet.');
		}).catch(() => this._toast('Could not join the plan sheet.'));
	}

	declineInvite(pIDInvitation)
	{
		this._tenantAPI('Account/Invitations/' + pIDInvitation + '/Decline', 'POST').then((pResult) =>
		{
			if (pResult.ok && pResult.body && pResult.body.Success) { this._loadInvitations(); return; }
			this._toast((pResult.body && pResult.body.Error) || 'Could not dismiss the invitation.');
		}).catch(() => this._toast('Could not dismiss the invitation.'));
	}

	inviteToTenant()
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.show !== 'function') { return; }
		this._closeSessionCard();
		// Close the mobile menu modal first when invoked from it, so the invite form is not stacked.
		if (typeof tmpModal.dismissModals === 'function') { tmpModal.dismissModals(); }
		let tmpEmailID = 'cst-invite-email';
		let tmpRoleID = 'cst-invite-role';
		let tmpEmail = '';
		let tmpRole = 'member';
		tmpModal.show(
			{
				title: 'Invite to this plan sheet',
				content: '<div class="cst-tenant-form">' +
					'<label for="' + tmpEmailID + '">Email</label>' +
					'<input id="' + tmpEmailID + '" type="email" autocomplete="off" placeholder="teammate@example.com" />' +
					'<label for="' + tmpRoleID + '">Role</label>' +
					'<select id="' + tmpRoleID + '"><option value="member">Member</option><option value="admin">Admin</option><option value="viewer">Viewer</option><option value="owner">Owner</option></select>' +
					'<p class="cst-tenant-form-hint">They join when they accept, with their own persona in this plan sheet.</p>' +
				'</div>',
				buttons: [ { Hash: 'invite', Label: 'Send invitation', Style: 'primary' }, { Hash: 'cancel', Label: 'Cancel' } ],
				closeable: true,
				onOpen: () =>
				{
					let tmpInput = document.getElementById(tmpEmailID);
					if (tmpInput) { tmpInput.focus(); tmpInput.addEventListener('input', () => { tmpEmail = String(tmpInput.value || '').trim(); }); }
					let tmpSelect = document.getElementById(tmpRoleID);
					if (tmpSelect) { tmpSelect.addEventListener('change', () => { tmpRole = tmpSelect.value; }); }
				}
			}).then((pHash) =>
			{
				if (pHash !== 'invite' || !tmpEmail) { return; }
				this._tenantAPI('Org/Invitations', 'POST', { Email: tmpEmail, Role: tmpRole }).then((pResult) =>
				{
					if (pResult.ok && pResult.body && pResult.body.Success)
					{
						if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast('Invitation sent to ' + tmpEmail, { type: 'success' }); }
						return;
					}
					this._toast((pResult.body && pResult.body.Error) || 'Could not send the invitation.');
				}).catch(() => this._toast('Could not send the invitation.'));
			});
	}

	_sessionCardHTML(pUser)
	{
		let tmpAvatar = libAvatarHelper.avatarHTML(pUser, { Size: 56 });
		let tmpTitle = _escapeHTML(pUser.Title || '');
		let tmpRows = '';
		let fRow = (pLabel, pValue) =>
		{
			tmpRows += '<div class="cst-acct-row"><span class="cst-acct-k">' + _escapeHTML(pLabel) + '</span><span class="cst-acct-v">' + _escapeHTML(pValue) + '</span></div>';
		};
		fRow('Email', pUser.Email || '—');
		if (pUser.CustomerName) { fRow('Organization', pUser.CustomerName); }
		fRow('Role', pUser.RoleLabel || 'Member');
		fRow('Last signed in', this._formatWhen(pUser.LastLoginDate));
		// Filled asynchronously by _loadInvitations; hidden until there are any.
		let tmpInvites = '<div class="cst-acct-invites" id="cst-acct-invites" style="display:none"></div>';
		let tmpSwitcher = '<div class="cst-acct-tenants">' +
			'<div class="cst-acct-tenants-h">Plan sheets</div>' +
			'<div class="cst-acct-tenant-list" id="cst-acct-tenant-list"><div class="cst-acct-tenant-empty">Loading&hellip;</div></div>' +
			'<button class="cst-acct-tenant-viewall" id="cst-acct-tenant-viewall" onclick="_Pict.views[\'Plansheet-TopBar-User\'].viewAllPlansheets()">Your plan sheets</button>' +
			'<button class="cst-acct-new" onclick="_Pict.views[\'Plansheet-TopBar-User\'].createTenant()">+ New plan sheet</button>' +
		'</div>';
		// Color-mode control, mirroring Settings > Appearance so the choice is one click from the name chip.
		let tmpMode = '';
		try
		{
			let tmpTheme = this.pict.providers.Theme;
			let tmpCurrent = (tmpTheme && typeof tmpTheme.getActiveTheme === 'function' && (tmpTheme.getActiveTheme() || {}).Mode) || 'system';
			let fSeg = (pMode, pLabel) => '<button class="cst-acct-mode-btn' + (tmpCurrent === pMode ? ' on' : '') + '" data-mode="' + pMode + '" onclick="_Pict.views[\'Plansheet-TopBar-User\'].setColorMode(\'' + pMode + '\')">' + pLabel + '</button>';
			tmpMode = '<div class="cst-acct-mode">' +
				'<div class="cst-acct-mode-h">Appearance</div>' +
				'<div class="cst-acct-mode-seg" role="group" aria-label="Color mode">' + fSeg('light', 'Light') + fSeg('dark', 'Dark') + fSeg('system', 'System') + '</div>' +
			'</div>';
		}
		catch (pErr) { tmpMode = ''; }
		let tmpActions = '<div class="cst-acct-actions">' +
			'<button class="cst-acct-act" onclick="_Pict.views[\'Plansheet-TopBar-User\'].gotoProfile()">View profile</button>' +
			'<button class="cst-acct-act" onclick="_Pict.views[\'Plansheet-TopBar-User\'].gotoSettings()">Settings</button>' +
			'<button class="cst-acct-act cst-acct-act-danger" onclick="_Pict.views[\'Plansheet-TopBar-User\'].signOut()">Sign out</button>' +
		'</div>';
		return '<div class="cst-acct">' +
			'<div class="cst-acct-head">' +
				'<span class="cst-acct-avatar">' + tmpAvatar + '</span>' +
				'<span class="cst-acct-id"><span class="cst-acct-name">' + _escapeHTML(pUser.DisplayName || pUser.Email || 'You') + '</span>' +
					(tmpTitle ? '<span class="cst-acct-title">' + tmpTitle + '</span>' : '') +
				'</span>' +
			'</div>' +
			'<div class="cst-acct-rows">' + tmpRows + '</div>' +
			tmpInvites +
			tmpSwitcher +
			tmpMode +
			tmpActions +
		'</div>';
	}

	// Set the color mode (light | dark | system) from the account flyout, the same provider call the
	// Appearance screen's ModeToggle makes. The provider's onApply reskins the app; we just repaint the
	// active segment here so the flyout stays in step without a full rebuild.
	setColorMode(pMode)
	{
		try
		{
			let tmpTheme = this.pict.providers.Theme;
			if (tmpTheme && typeof tmpTheme.setMode === 'function') { tmpTheme.setMode(pMode); }
			if (typeof document !== 'undefined')
			{
				let tmpBtns = document.querySelectorAll('.cst-acct-mode-btn');
				for (let i = 0; i < tmpBtns.length; i++)
				{
					tmpBtns[i].classList.toggle('on', tmpBtns[i].getAttribute('data-mode') === pMode);
				}
			}
		}
		catch (pErr) { /* theme provider unavailable */ }
	}

	// Format an ISO timestamp for the card. Empty means this is the first sign-in (the previous-login
	// marker has not been set yet); a bad value falls back to the raw string.
	_formatWhen(pISO)
	{
		if (!pISO) { return 'First sign-in'; }
		let tmpDate = new Date(pISO);
		if (isNaN(tmpDate.getTime())) { return String(pISO); }
		try { return tmpDate.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
		catch (pErr) { return tmpDate.toLocaleString(); }
	}

	onBeforeRender(pRenderable)
	{
		this._refreshSlots();
		return super.onBeforeRender(pRenderable);
	}

	// Paint the avatar into its slot from AppData.Auth.User. Called on render and
	// can be re-called (e.g. after a photo change in User Settings) to refresh it.
	paintAvatar()
	{
		if (typeof document === 'undefined') { return; }
		let tmpEl = document.getElementById('Plansheet-TopBar-Avatar');
		if (!tmpEl) { return; }
		let tmpAuth = this.pict.AppData.Auth || {};
		if (tmpAuth.LoggedIn && tmpAuth.User)
		{
			tmpEl.innerHTML = libAvatarHelper.avatarHTML(tmpAuth.User, { Size: 26 });
		}
		else
		{
			tmpEl.innerHTML = '';
		}
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		// A re-render replaces the chip the popover was anchored to; close it so it cannot orphan.
		this._closeSessionCard();
		this.paintAvatar();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewTopBarUser;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-TopBar-User',
	DefaultRenderable: 'Plansheet-TopBar-User',
	DefaultDestinationAddress: '#Theme-TopBar-User',
	AutoRender:                false,
	CSS: /*css*/`
		.cst-user { display: flex; align-items: center; height: 100%; gap: 8px; padding: 0 12px; }
		.cst-user-avatar { display: inline-flex; align-items: center; }
		.cst-user-name { font-size: 13px; color: var(--theme-color-text-on-brand, var(--theme-color-text-secondary, #1d2230)); padding: 0 4px; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.cst-user-btn {
			padding: 4px 8px;
			border: 1px solid var(--theme-color-border-default, #5E5549);
			background: transparent;
			color: var(--theme-color-text-on-brand,
				   var(--theme-color-text-secondary, #1d2230));
			border-radius: 4px;
			cursor: pointer;
			display: inline-flex; align-items: center; justify-content: center;
			font-size: 1em; line-height: 1;
		}
		.cst-user-btn:hover {
			background: var(--theme-color-background-hover, rgba(255,255,255,0.08));
		}
		.cst-user-btn-text { padding: 4px 10px; font-size: 13px; }
		/* The signed-in chip is a button that opens the account/session card popover on click. */
		.cst-user-chip { display: inline-flex; align-items: center; gap: 8px; height: 100%; padding: 0 8px; border: none; background: transparent; color: inherit; border-radius: 6px; cursor: pointer; font: inherit; }
		.cst-user-chip:hover, .cst-user-chip:focus-visible { background: var(--theme-color-background-hover, rgba(255,255,255,0.10)); }
		/* Account/session card, shown in the click-toggled popover panel (.cst-acct-popover). */
		.cst-acct { display: flex; flex-direction: column; gap: 14px; padding: 16px; min-width: 250px; }
		.cst-acct-head { display: flex; align-items: center; gap: 12px; }
		.cst-acct-avatar { display: inline-flex; }
		.cst-acct-id { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
		.cst-acct-name { font-size: 16px; font-weight: 600; color: var(--theme-color-text-primary, #1d2230); }
		.cst-acct-title { font-size: 13px; color: var(--theme-color-text-secondary, #5b6376); }
		.cst-acct-rows { display: flex; flex-direction: column; }
		.cst-acct-row { display: flex; justify-content: space-between; gap: 16px; font-size: 13px; padding: 8px 0; border-top: 1px solid var(--theme-color-border-subtle, #eef1f6); }
		.cst-acct-row:first-child { border-top: none; }
		.cst-acct-k { color: var(--theme-color-text-secondary, #5b6376); }
		.cst-acct-v { color: var(--theme-color-text-primary, #1d2230); font-weight: 500; text-align: right; overflow: hidden; text-overflow: ellipsis; }
		.cst-acct-actions { display: flex; gap: 8px; padding-top: 4px; }
		.cst-acct-act { flex: 1 1 auto; padding: 7px 10px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1d2230); cursor: pointer; font-size: 12px; line-height: 1; }
		.cst-acct-act:hover { background: var(--theme-color-background-hover, #f2f4f8); }
		.cst-acct-act-danger { color: var(--theme-color-status-danger, #b3261e); border-color: var(--theme-color-status-danger, #f0c2bd); }
		.cst-acct-act-danger:hover { background: rgba(179,38,30,0.06); }
		/* Appearance: a three-segment Light / Dark / System control, mirroring Settings > Appearance. */
		.cst-acct-mode { padding-top: 6px; }
		.cst-acct-mode-h { font-family: var(--plansheet-gorton); font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: var(--theme-color-text-secondary, #5b6376); margin: 0 2px 6px; }
		.cst-acct-mode-seg { display: flex; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 6px; overflow: hidden; }
		.cst-acct-mode-btn { flex: 1 1 0; padding: 6px 8px; border: 0; border-right: 1px solid var(--theme-color-border-default, #d8dde6); background: transparent; color: var(--theme-color-text-secondary, #5b6376); font-family: var(--plansheet-gorton); text-transform: uppercase; letter-spacing: 0.7px; font-size: 10.5px; cursor: pointer; line-height: 1.4; }
		.cst-acct-mode-btn:last-child { border-right: 0; }
		.cst-acct-mode-btn:hover { background: var(--theme-color-background-hover, #f2f4f8); color: var(--theme-color-text-primary, #1d2230); }
		.cst-acct-mode-btn.on { background: var(--theme-color-brand-primary, #4f95e8); color: #fff; }
		/* The click-toggled popover panel: a fixed, non-blocking card anchored under the chip. */
		.cst-acct-popover { position: fixed; z-index: 1100; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1d2230); border: 1px solid var(--theme-color-border-default, #e6e9ee); border-radius: 12px; box-shadow: 0 10px 30px rgba(13,18,28,0.18); max-width: 340px; }
		/* Plan-sheet switcher inside the account card. */
		.cst-acct-tenants { display: flex; flex-direction: column; gap: 6px; padding-top: 12px; border-top: 1px solid var(--theme-color-border-subtle, #eef1f6); }
		.cst-acct-tenants-h { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); }
		.cst-acct-tenant-list { display: flex; flex-direction: column; gap: 2px; max-height: 220px; overflow-y: auto; }
		.cst-acct-tenant { display: flex; align-items: center; gap: 8px; padding: 7px 8px; border-radius: 8px; cursor: pointer; }
		.cst-acct-tenant:hover { background: var(--theme-color-background-hover, #f2f4f8); }
		.cst-acct-tenant.is-current { background: var(--theme-color-background-selected, rgba(55,138,221,0.10)); cursor: default; }
		.cst-acct-tenant-main { display: flex; flex-direction: column; gap: 1px; min-width: 0; flex: 1 1 auto; }
		.cst-acct-tenant-name { font-size: 13px; font-weight: 600; color: var(--theme-color-text-primary, #1d2230); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.cst-acct-tenant-sub { font-size: 11px; color: var(--theme-color-text-secondary, #5b6376); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.cst-acct-tenant-go { font-size: 11px; color: var(--theme-color-text-secondary, #5b6376); opacity: 0; }
		.cst-acct-tenant:hover .cst-acct-tenant-go { opacity: 1; }
		.cst-acct-tenant-leave { font-size: 11px; padding: 3px 8px; border: 1px solid var(--theme-color-status-danger, #f0c2bd); border-radius: 6px; background: transparent; color: var(--theme-color-status-danger, #b3261e); cursor: pointer; }
		.cst-acct-tenant-leave:hover { background: rgba(179,38,30,0.06); }
		/* Per-row pin toggle in the switcher (WI #339): faint until hovered, amber when pinned. */
		.cst-acct-tenant-pin { flex: 0 0 auto; display: inline-grid; place-items: center; width: 24px; height: 24px; border: none; border-radius: 6px; background: transparent; color: var(--theme-color-text-secondary, #a2acbd); cursor: pointer; opacity: 0; padding: 0; }
		.cst-acct-tenant:hover .cst-acct-tenant-pin { opacity: 1; }
		.cst-acct-tenant-pin.on { opacity: 1; color: var(--theme-color-status-warning, #b9812f); }
		.cst-acct-tenant-pin:hover { background: var(--theme-color-background-hover, #f2f4f8); color: var(--theme-color-brand, #378add); }
		.cst-acct-tenant-empty { font-size: 12px; color: var(--theme-color-text-secondary, #5b6376); padding: 6px 8px; }
		.cst-acct-new { margin-top: 2px; padding: 7px 10px; border: 1px dashed var(--theme-color-border-default, #d8dde6); border-radius: 8px; background: transparent; color: var(--theme-color-text-primary, #1d2230); cursor: pointer; font-size: 12px; text-align: center; }
		.cst-acct-new:hover { background: var(--theme-color-background-hover, #f2f4f8); border-style: solid; }
		/* "View all N plan sheets" link when compact list is truncated. */
		.cst-acct-tenant-viewall { display: block; width: 100%; margin-top: 8px; padding: 8px 10px; border: 1px solid var(--theme-color-brand, #378add); border-radius: 8px; background: var(--theme-color-brand, #378add); color: var(--theme-color-text-on-brand, #ffffff); cursor: pointer; font-size: 12px; font-weight: 600; text-align: center; }
		.cst-acct-tenant-viewall:hover { filter: brightness(1.06); }
		/* Create-plan-sheet modal form. */
		.cst-tenant-form { display: flex; flex-direction: column; gap: 8px; min-width: 320px; }
		.cst-tenant-form label { font-size: 12px; font-weight: 600; color: var(--theme-color-text-secondary, #5b6376); }
		.cst-tenant-form input { padding: 9px 11px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; font-size: 14px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1d2230); }
		.cst-tenant-form input:focus { outline: none; border-color: var(--theme-color-brand, #378add); }
		.cst-tenant-form-hint { font-size: 12px; color: var(--theme-color-text-secondary, #5b6376); margin: 0; }
		.cst-tenant-form select { padding: 9px 11px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; font-size: 14px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1d2230); }
		/* Pending invitations section in the account card. */
		.cst-acct-invites { display: flex; flex-direction: column; gap: 6px; padding-top: 12px; border-top: 1px solid var(--theme-color-border-subtle, #eef1f6); }
		.cst-acct-invite { display: flex; align-items: center; gap: 8px; padding: 7px 8px; border-radius: 8px; background: var(--theme-color-background-info-subtle, rgba(55,138,221,0.06)); }
		.cst-acct-invite-actions { display: inline-flex; gap: 6px; flex: 0 0 auto; }
		.cst-acct-invite-accept { font-size: 11px; padding: 4px 10px; border: none; border-radius: 6px; background: var(--theme-color-brand, #378add); color: var(--theme-color-text-on-brand, #fff); cursor: pointer; }
		.cst-acct-invite-accept:hover { filter: brightness(1.05); }
		.cst-acct-invite-decline { font-size: 11px; padding: 4px 8px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 6px; background: transparent; color: var(--theme-color-text-secondary, #5b6376); cursor: pointer; }
		.cst-acct-invite-decline:hover { background: var(--theme-color-background-hover, #f2f4f8); }
		.cst-acct-tenant-invite { font-size: 11px; padding: 3px 8px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 6px; background: transparent; color: var(--theme-color-text-primary, #1d2230); cursor: pointer; }
		.cst-acct-tenant-invite:hover { background: var(--theme-color-background-hover, #f2f4f8); }
		/* Mobile menu: the top-bar burger override (app.openMobileMenu) renders the full nav + account
		   actions into a pict-section-modal. Reuses the .cst-acct-tenant* + .cst-acct-new styles above.
		   It must scroll: the nav groups plus the plan-sheet switcher at the bottom run taller than a
		   phone viewport, and without an overflow the switcher ("Your plan sheets") is stranded below the
		   fold with no way to reach it. Bound the height and scroll within the modal. */
		.cst-mnav { display: flex; flex-direction: column; max-height: 72vh; overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; }
		.cst-mnav-group { display: flex; flex-direction: column; gap: 2px; padding: 8px 0; border-top: 1px solid var(--theme-color-border-subtle, #eef1f6); }
		.cst-mnav-group:first-child { border-top: none; padding-top: 0; }
		.cst-mnav-group-h { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); padding: 2px 6px 4px; }
		.cst-mnav-item { display: flex; align-items: center; width: 100%; text-align: left; padding: 11px 10px; border: none; background: transparent; color: var(--theme-color-text-primary, #1d2230); border-radius: 8px; cursor: pointer; font: inherit; font-size: 14px; line-height: 1.1; }
		.cst-mnav-item:hover, .cst-mnav-item:active { background: var(--theme-color-background-hover, #f2f4f8); }
		.cst-mnav-danger { color: var(--theme-color-status-danger, #b3261e); }
	`,
	Templates: [
		{
			Hash: 'Plansheet-TopBar-User',
			Template: /*html*/`
<div class="cst-user">
	{~TS:TopBar-User-LoggedIn:AppData.TopBarUser.LoggedInSlot~}
	{~TS:TopBar-User-Guest:AppData.TopBarUser.GuestSlot~}
</div>`
		},
		{
			Hash: 'TopBar-User-LoggedIn',
			// Clicking the chip toggles the account/session card popover (toggleSessionCard); profile,
			// settings, and sign out live in there.
			Template: /*html*/`
<button class="cst-user-chip" id="Plansheet-TopBar-Chip" onclick="_Pict.views['Plansheet-TopBar-User'].toggleSessionCard()" title="Your account" aria-haspopup="true">
	<span class="cst-user-avatar" id="Plansheet-TopBar-Avatar"></span>
	<span class="cst-user-name" title="{~D:Record.DisplayName~}">{~D:Record.DisplayName~}</span>
</button>`
		},
		{
			Hash: 'TopBar-User-Guest',
			Template: /*html*/`<button class="cst-user-btn cst-user-btn-text" onclick="_Pict.views['Plansheet-TopBar-User'].signIn()" title="Sign in">Sign in</button>`
		}
	],
	Renderables: [{
		RenderableHash: 'Plansheet-TopBar-User',
		TemplateHash: 'Plansheet-TopBar-User',
		ContentDestinationAddress: '#Theme-TopBar-User'
	}]
};
